import { initializeApp, type FirebaseApp } from 'firebase/app'
import { getAuth, type Auth } from 'firebase/auth'
import { getFirestore, enableMultiTabIndexedDbPersistence, type Firestore } from 'firebase/firestore'
import { getStorage, type FirebaseStorage } from 'firebase/storage'
import { getMessaging, type Messaging } from 'firebase/messaging'
import { getAnalytics, type Analytics } from 'firebase/analytics'

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
}

// Check if we have valid Firebase config
const hasValidConfig = (): boolean => {
  return !!(
    firebaseConfig.apiKey &&
    firebaseConfig.apiKey !== 'your_api_key_here' &&
    firebaseConfig.apiKey !== 'mock-api-key-for-development-only' &&
    firebaseConfig.projectId &&
    firebaseConfig.projectId !== 'your_project_id'
  )
}

// Initialize Firebase with error handling
let app: FirebaseApp | null = null
let auth: Auth | null = null
let db: Firestore | null = null
let storage: FirebaseStorage | null = null
let messaging: Messaging | null = null
let analytics: Analytics | null = null
let firebaseError: string | null = null
let isFirebaseAvailable = false

try {
  if (typeof window !== 'undefined') {
    if (!hasValidConfig()) {
      firebaseError = 'Firebase not configured. Please add your Firebase credentials to the .env file.'
      console.warn('[Firebase] Invalid or missing configuration:', firebaseError)
    } else {
      app = initializeApp(firebaseConfig)
      auth = getAuth(app)
      db = getFirestore(app)
      storage = getStorage(app)
      messaging = getMessaging(app)
      
      if (import.meta.env.VITE_ENABLE_ANALYTICS === 'true') {
        analytics = getAnalytics(app)
      }
      
      isFirebaseAvailable = true
      
      // Enable offline persistence for Firestore
      enableMultiTabIndexedDbPersistence(db).catch((err: { code?: string }) => {
        if (err.code === 'failed-precondition') {
          console.warn('[Firebase] Multiple tabs open, persistence can only be enabled in one tab at a time.')
        } else if (err.code === 'unimplemented') {
          console.warn('[Firebase] The current browser does not support offline persistence.')
        }
      })
    }
  }
} catch (error) {
  firebaseError = error instanceof Error ? error.message : 'Unknown Firebase initialization error'
  console.error('[Firebase] Initialization failed:', firebaseError)
  isFirebaseAvailable = false
}

// Export initialization status helpers
export const getFirebaseStatus = () => ({
  isAvailable: isFirebaseAvailable,
  error: firebaseError,
  isConfigured: hasValidConfig()
})

export { app, auth, db, storage, messaging, analytics, isFirebaseAvailable, firebaseError }
export default app
